import React from "react";
import { API_BASE } from "@/lib/api";

/**
 * Stylised vinyl disc.
 * If `coverSong` is provided AND it has a cover, the centre label becomes that image (clipped to a circle).
 * Otherwise the centre shows the song title's first letter.
 * Rotates smoothly while `playing`, eases to stop when paused.
 */
export default function VinylDisc({ title, playing, coverSong = null, localCoverUrl = null }) {
  const ch = (title || "?").trim().slice(0, 1).toUpperCase();
  const hasCover =
    !!localCoverUrl || (coverSong && coverSong.has_cover && coverSong.id);
  const coverUrl =
    localCoverUrl ||
    (hasCover
      ? `${API_BASE}/songs/${coverSong.id}/cover${
          coverSong.cover_updated_at
            ? `?v=${encodeURIComponent(coverSong.cover_updated_at)}`
            : ""
        }`
      : null);

  return (
    <div className="relative w-full aspect-square select-none" data-testid="vinyl-disc">
      {/* Vinyl base */}
      <div
        className={`absolute inset-0 rounded-full bg-[#0E0E0E] border border-white/10 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.8)] spin-slow ${
          playing ? "" : "spin-paused"
        }`}
        style={{
          backgroundImage: `
            repeating-radial-gradient(circle at center, rgba(255,255,255,0.02) 0 2px, transparent 2px 4px),
            radial-gradient(circle at 30% 30%, rgba(255,255,255,0.05), transparent 50%),
            radial-gradient(circle at 70% 75%, rgba(255,59,48,0.06), transparent 55%)
          `,
        }}
      >
        {/* Grooves */}
        <div className="absolute inset-[6%] rounded-full border border-white/5" />
        <div className="absolute inset-[14%] rounded-full border border-white/5" />
        <div className="absolute inset-[22%] rounded-full border border-white/5" />
        <div className="absolute inset-[30%] rounded-full border border-white/5" />

        {/* Centre label */}
        <div
          className={`absolute inset-[28%] rounded-full ${
            hasCover ? "bg-[#0F0F0F]" : "bg-[#FF3B30]"
          } flex items-center justify-center overflow-hidden`}
        >
          {hasCover ? (
            <img
              src={coverUrl}
              alt={title || "cover"}
              className="w-full h-full object-cover"
              crossOrigin="anonymous"
              data-testid="vinyl-cover-img"
            />
          ) : (
            <>
              <div
                className="absolute inset-0 opacity-25"
                style={{
                  background:
                    "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.5), transparent 60%)",
                }}
              />
              <span className="font-display font-black text-5xl sm:text-6xl text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]">
                {ch}
              </span>
            </>
          )}
        </div>

        {/* Centre hole */}
        <div className="absolute inset-[46%] rounded-full bg-[#0A0A0A] border border-white/10" />

        {/* Light reflection arc */}
        <div
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            background:
              "conic-gradient(from 220deg, transparent 0deg, rgba(255,255,255,0.12) 25deg, transparent 60deg, transparent 360deg)",
            mixBlendMode: "screen",
          }}
        />
      </div>

      {/* Tonearm */}
      <div
        className="absolute top-[6%] right-[2%] w-1/2 origin-top-right transition-transform duration-700 ease-out"
        style={{ transform: `rotate(${playing ? -28 : -52}deg)` }}
        aria-hidden
      >
        <div className="relative">
          <div className="absolute right-0 top-0 h-3 w-3 rounded-full bg-[#222] border border-white/10" />
          <div className="h-[3px] bg-gradient-to-l from-white/40 via-white/15 to-transparent ml-3 rounded-full" />
          <div className="absolute left-0 -top-1 h-3 w-1 bg-[#FF3B30] rounded-sm" />
        </div>
      </div>
    </div>
  );
}
